import axios from 'axios';
import { ValidationError, type ValidationFieldError } from './ValidationError';

export class ApiError extends Error {
    status: number;

    data: unknown;

    constructor(message: string, status: number, data: unknown) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
    }

    /**
     * Wraps an unknown error as an `ApiError`.
     * Returns the error as-is if already an `ApiError`,
     * converts Axios errors with a response, or returns `null`.
     */
    static from(error: unknown): ApiError | null {
        if (error instanceof ApiError) return error;
        if (axios.isAxiosError(error) && error.response) {
            return new ApiError(
                error.response.statusText || 'Request failed',
                error.response.status,
                error.response.data,
            );
        }
        return null;
    }

    /**
     * Parses the 422 response body into a `ValidationError`.
     *
     * Supports two backend formats:
     *  - Array:  `{ errors: [{ field, messages }] }`
     *  - Object: `{ errors: { field: string[] } }`
     *
     * @param fieldMap – optional map of API field names → frontend field names.
     */
    toValidationError(fieldMap?: Record<string, string>): ValidationError {
        const raw = this.data as { errors?: unknown } | undefined;
        const errors: ValidationFieldError[] = [];

        if (Array.isArray(raw?.errors)) {
            for (const item of raw!.errors) {
                if (typeof item === 'object' && item !== null && typeof item.field === 'string') {
                    errors.push({
                        field: fieldMap?.[item.field] ?? item.field,
                        messages: Array.isArray(item.messages) ? item.messages : [],
                    });
                }
            }
        } else if (raw?.errors && typeof raw.errors === 'object') {
            for (const [key, msgs] of Object.entries(raw.errors as Record<string, string[] | string>)) {
                errors.push({
                    field: fieldMap?.[key] ?? key,
                    messages: Array.isArray(msgs) ? msgs : [msgs],
                });
            }
        }

        return new ValidationError(errors);
    }
}